// Copied from tremor
import React from "react"
import {
  Area,
  Bar,
  Line,
  AreaChart as RechartsAreaChart,
  BarChart as RechartsBarChart,
  LineChart as RechartsLineChart,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from "recharts"
import { type AxisDomain } from "recharts/types/util/types"

// Define utility functions and types locally instead of importing from external libs
type AvailableChartColorsKeys =
  | "blue"
  | "green"
  | "red"
  | "orange"
  | "purple"
  | "teal"
  | "yellow"
  | "gray"

const AvailableChartColors: AvailableChartColorsKeys[] = [
  "blue",
  "green",
  "red",
  "orange",
  "purple",
  "teal",
  "yellow",
  "gray",
]

// Utility function to combine class names
const cx = (...classNames: (string | undefined | null | false)[]) => {
  return classNames.filter(Boolean).join(" ")
}

// Utility function to get color class name
const getColorClassName = (
  color: AvailableChartColorsKeys,
  type: "fill" | "stroke" | "text"
): string => {
  const colorMap: Record<AvailableChartColorsKeys, Record<string, string>> = {
    blue: {
      fill: "fill-blue-600 dark:fill-blue-400",
      stroke: "stroke-blue-600 dark:stroke-blue-400",
      text: "text-blue-600 dark:text-blue-400",
    },
    green: {
      fill: "fill-emerald-600 dark:fill-emerald-400",
      stroke: "stroke-emerald-600 dark:stroke-emerald-400",
      text: "text-emerald-600 dark:text-emerald-400",
    },
    red: {
      fill: "fill-red-600 dark:fill-red-400",
      stroke: "stroke-red-600 dark:stroke-red-400",
      text: "text-red-600 dark:text-red-400",
    },
    orange: {
      fill: "fill-orange-600 dark:fill-orange-400",
      stroke: "stroke-orange-600 dark:stroke-orange-400",
      text: "text-orange-600 dark:text-orange-400",
    },
    purple: {
      fill: "fill-violet-600 dark:fill-violet-400",
      stroke: "stroke-violet-600 dark:stroke-violet-400",
      text: "text-violet-600 dark:text-violet-400",
    },
    teal: {
      fill: "fill-cyan-600 dark:fill-cyan-400",
      stroke: "stroke-cyan-600 dark:stroke-cyan-400",
      text: "text-cyan-600 dark:text-cyan-400",
    },
    yellow: {
      fill: "fill-yellow-600 dark:fill-yellow-400",
      stroke: "stroke-yellow-600 dark:stroke-yellow-400",
      text: "text-yellow-600 dark:text-yellow-400",
    },
    gray: {
      fill: "fill-neutral-600 dark:fill-neutral-400",
      stroke: "stroke-neutral-600 dark:stroke-neutral-400",
      text: "text-neutral-600 dark:text-neutral-400",
    },
  }

  return colorMap[color][type]
}

// Utility function to construct category colors
const constructCategoryColors = (
  categories: string[],
  colors: AvailableChartColorsKeys[]
): Map<string, AvailableChartColorsKeys> => {
  const categoryColors = new Map<string, AvailableChartColorsKeys>()

  categories.forEach((category, index) => {
    categoryColors.set(category, colors[index % colors.length])
  })

  return categoryColors
}

// Utility function to get Y-axis domain
const getYAxisDomain = (
  autoMinValue: boolean,
  minValue?: number,
  maxValue?: number
): [number | "auto", number | "auto"] => {
  return [autoMinValue ? "auto" : (minValue ?? 0), maxValue ?? "auto"]
}

//#region SparkAreaChart

interface SparkAreaChartProps extends React.HTMLAttributes<HTMLDivElement> {
  data: Record<string, any>[]
  categories: string[]
  index: string
  colors?: AvailableChartColorsKeys[]
  autoMinValue?: boolean
  minValue?: number
  maxValue?: number
  connectNulls?: boolean
  type?: "default" | "stacked" | "percent"
  fill?: "gradient" | "solid" | "none"
}

const SparkAreaChart = React.forwardRef<HTMLDivElement, SparkAreaChartProps>(
  (props, forwardedRef) => {
    const {
      data = [],
      categories = [],
      index,
      colors = AvailableChartColors,
      autoMinValue = false,
      minValue,
      maxValue,
      connectNulls = false,
      type = "default",
      className,
      fill = "gradient",
      ...other
    } = props

    const categoryColors = constructCategoryColors(categories, colors)
    const yAxisDomain = getYAxisDomain(autoMinValue, minValue, maxValue)
    const stacked = type === "stacked" || type === "percent"
    const areaId = React.useId()

    const getFillContent = (fillType: SparkAreaChartProps["fill"]) => {
      switch (fillType) {
        case "none":
          return <stop stopColor="currentColor" stopOpacity={0} />
        case "gradient":
          return (
            <>
              <stop offset="5%" stopColor="currentColor" stopOpacity={0.4} />
              <stop offset="95%" stopColor="currentColor" stopOpacity={0} />
            </>
          )
        case "solid":
          return <stop stopColor="currentColor" stopOpacity={0.3} />
        default:
          return <stop stopColor="currentColor" stopOpacity={0.3} />
      }
    }

    return (
      <div
        ref={forwardedRef}
        className={cx("h-12 w-28", className)}
        tremor-id="tremor-raw"
        {...other}
      >
        <ResponsiveContainer>
          <RechartsAreaChart
            data={data}
            margin={{
              bottom: 1,
              left: 1,
              right: 1,
              top: 1,
            }}
            stackOffset={type === "percent" ? "expand" : undefined}
          >
            <XAxis hide dataKey={index} />
            <YAxis hide={true} domain={yAxisDomain as AxisDomain} />

            {categories.map((category) => {
              const categoryId = `${areaId}-${category.replace(/[^a-zA-Z0-9]/g, "")}`
              return (
                <React.Fragment key={category}>
                  <defs>
                    <linearGradient
                      key={category}
                      className={cx(
                        getColorClassName(
                          categoryColors.get(
                            category
                          ) as AvailableChartColorsKeys,
                          "text"
                        )
                      )}
                      id={categoryId}
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      {getFillContent(fill)}
                    </linearGradient>
                  </defs>
                  <Area
                    className={cx(
                      getColorClassName(
                        categoryColors.get(
                          category
                        ) as AvailableChartColorsKeys,
                        "stroke"
                      )
                    )}
                    dot={false}
                    strokeOpacity={1}
                    name={category}
                    type="linear"
                    dataKey={category}
                    stroke=""
                    strokeWidth={1}
                    strokeLinejoin="round"
                    strokeLinecap="round"
                    isAnimationActive={false}
                    connectNulls={connectNulls}
                    stackId={stacked ? "stack" : undefined}
                    fill={`url(#${categoryId})`}
                  />
                </React.Fragment>
              )
            })}
          </RechartsAreaChart>
        </ResponsiveContainer>
      </div>
    )
  }
)

SparkAreaChart.displayName = "SparkAreaChart"

//#region SparkLineChart

interface SparkLineChartProps extends React.HTMLAttributes<HTMLDivElement> {
  data: Record<string, any>[]
  categories: string[]
  index: string
  colors?: AvailableChartColorsKeys[]
  autoMinValue?: boolean
  minValue?: number
  maxValue?: number
  connectNulls?: boolean
}

const SparkLineChart = React.forwardRef<HTMLDivElement, SparkLineChartProps>(
  (props, forwardedRef) => {
    const {
      data = [],
      categories = [],
      index,
      colors = AvailableChartColors,
      autoMinValue = false,
      minValue,
      maxValue,
      connectNulls = false,
      className,
      ...other
    } = props

    const categoryColors = constructCategoryColors(categories, colors)
    const yAxisDomain = getYAxisDomain(autoMinValue, minValue, maxValue)

    return (
      <div
        ref={forwardedRef}
        className={cx("h-12 w-28", className)}
        tremor-id="tremor-raw"
        {...other}
      >
        <ResponsiveContainer>
          <RechartsLineChart
            data={data}
            margin={{
              bottom: 1,
              left: 1,
              right: 1,
              top: 1,
            }}
          >
            <XAxis hide dataKey={index} />
            <YAxis hide={true} domain={yAxisDomain as AxisDomain} />
            {categories.map((category) => (
              <Line
                className={cx(
                  getColorClassName(
                    categoryColors.get(category) as AvailableChartColorsKeys,
                    "stroke"
                  )
                )}
                dot={false}
                strokeOpacity={1}
                key={category}
                name={category}
                type="linear"
                dataKey={category}
                stroke=""
                strokeWidth={2}
                strokeLinejoin="round"
                strokeLinecap="round"
                isAnimationActive={false}
                connectNulls={connectNulls}
              />
            ))}
          </RechartsLineChart>
        </ResponsiveContainer>
      </div>
    )
  }
)

SparkLineChart.displayName = "SparkLineChart"

//#region SparkBarChart

interface BarChartProps extends React.HTMLAttributes<HTMLDivElement> {
  data: Record<string, any>[]
  index: string
  categories: string[]
  colors?: AvailableChartColorsKeys[]
  autoMinValue?: boolean
  minValue?: number
  maxValue?: number
  barCategoryGap?: string | number
  type?: "default" | "stacked" | "percent"
}

const SparkBarChart = React.forwardRef<HTMLDivElement, BarChartProps>(
  (props, forwardedRef) => {
    const {
      data = [],
      categories = [],
      index,
      colors = AvailableChartColors,
      autoMinValue = false,
      minValue,
      maxValue,
      barCategoryGap,
      type = "default",
      className,
      ...other
    } = props

    const categoryColors = constructCategoryColors(categories, colors)

    const yAxisDomain = getYAxisDomain(autoMinValue, minValue, maxValue)
    const stacked = type === "stacked" || type === "percent"

    return (
      <div
        ref={forwardedRef}
        className={cx("h-12 w-28", className)}
        tremor-id="tremor-raw"
        {...other}
      >
        <ResponsiveContainer>
          <RechartsBarChart
            data={data}
            margin={{
              bottom: 1,
              left: 1,
              right: 1,
              top: 1,
            }}
            stackOffset={type === "percent" ? "expand" : undefined}
            barCategoryGap={barCategoryGap}
          >
            <XAxis hide dataKey={index} />
            <YAxis hide={true} domain={yAxisDomain as AxisDomain} />

            {categories.map((category) => (
              <Bar
                className={cx(
                  getColorClassName(
                    categoryColors.get(category) as AvailableChartColorsKeys,
                    "fill"
                  )
                )}
                key={category}
                name={category}
                type="linear"
                dataKey={category}
                stackId={stacked ? "stack" : undefined}
                isAnimationActive={false}
                fill=""
              />
            ))}
          </RechartsBarChart>
        </ResponsiveContainer>
      </div>
    )
  }
)

SparkBarChart.displayName = "SparkBarChart"

export { SparkAreaChart, SparkLineChart, SparkBarChart }
